import { makeStyles, Paper } from '@material-ui/core';
import { AxiosError } from 'axios';
import { DateTime, Info } from 'luxon';
import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Dispatch as UseDispatch } from 'redux';

import { engineerSummaryDomain } from 'models';
import Appointment from 'models/Appointment';
import Engineer, { EngineerSummary } from 'models/Engineers';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fetchEngineerSummary } from 'store/endpoints/engineerSummary';
import { useAppointmentsData, useEngineerData } from 'store/selectors';
import { getAvailabilityUtilisation } from 'store/slices/availabilityUtilisation';
import {
  commonErrorHandling,
  displayErrorSnackbar,
  getErrorMessageAndStatus
} from 'store/utils';
import { fetchAllEngineerFixedAppointments } from 'store/slices/appointments';
import { fetchAllSkills } from 'store/slices/skills';
import EngineerCalendarBody from './EngineerCalendarBody';
import EngineerCalendarHeader from './EngineerCalendarHeader';
import EngineerCalendarKey from './EngineerCalendarKey';
import {
  AdjustmentFormModals,
  TemporaryAdjustmentFormContextProvider
} from '../EngineerSchedule/AdjustmentFormModals';

export interface EngineerCalendarDatesState {
  start: DateTime;
  end: DateTime;
}

interface ResponseState<T = any[]> {
  isLoading: boolean;
  data: T | undefined;
  error: boolean;
}

export interface EngineerDailyUtilisation {
  date: string;
  utilisation?: number;
  availability?: number;
  percentageUtilisation?: number;
  error?: AxiosError | any;
}

export interface EngineerEvent extends Pick<Appointment, 'id' | 'reason'> {
  slot: {
    date: DateTime;
    startTime: DateTime;
    endTime: DateTime;
  };
}
interface EngineerCalendarContext {
  calendarAvailability: ResponseState<EngineerDailyUtilisation[]>;
  setCalendarAvailability?: Dispatch<
    SetStateAction<ResponseState<EngineerDailyUtilisation[]>>
  >;
  engineerData?: Engineer;
  engineerSummaries?: CalendarEngineerSummary[];
}

interface EngineerCalendarDatesContext {
  dates: EngineerCalendarDatesState;
  setDates?: Dispatch<SetStateAction<EngineerCalendarDatesState>>;
}

export interface CalendarEngineerSummary
  extends Pick<EngineerSummary, 'startTime' | 'endTime' | 'date' | 'patch'> {
  weekday: string;
}

const useEngineerCalendarStyles = makeStyles(
  theme => ({
    container: {
      maxWidth: theme.containers.large,
      minWidth: theme.containers.large
    }
  }),
  { name: 'EngineerCalendar' }
);

export const dateDiffUnit = 'week';

export const EngineerCalendarContext = React.createContext<
  EngineerCalendarContext
>({
  calendarAvailability: {
    isLoading: false,
    data: undefined,
    error: false
  },
  engineerData: undefined
});

export const EngineerCalendarDatesContext = React.createContext<
  EngineerCalendarDatesContext
>({
  dates: {
    start: DateTime.now().startOf(dateDiffUnit),
    end: DateTime.now().endOf(dateDiffUnit)
  }
});

function handleError(error: AxiosError | any, dispatch: UseDispatch) {
  const { message, status } = getErrorMessageAndStatus(error);

  displayErrorSnackbar(message, status, dispatch);
  commonErrorHandling(error, engineerSummaryDomain.type);
}

export function EngineerCalendarDatesProvider({
  children
}: {
  children: ReactNode;
}) {
  const [dates, setDates] = useState({
    start: DateTime.now().startOf(dateDiffUnit),
    end: DateTime.now().endOf(dateDiffUnit)
  });

  return (
    <EngineerCalendarDatesContext.Provider value={{ dates, setDates }}>
      {children}
    </EngineerCalendarDatesContext.Provider>
  );
}

function EngineerCalendar() {
  const classes = useEngineerCalendarStyles();
  const dispatch = useDispatch();
  const { dates } = useContext(EngineerCalendarDatesContext);

  const [calendarAvailability, setCalendarAvailability] = useState<
    ResponseState<EngineerDailyUtilisation[]>
  >({
    isLoading: false,
    data: undefined,
    error: false
  });

  const [engineerSummaries, setEngineerSummaries] = useState<
    CalendarEngineerSummary[]
  >([]);

  const [events, setEvents] = useState<EngineerEvent[]>([]);

  const { id: engineerId } = useParams<{ id: string }>();

  /**
   * This method fetches engineer summaries and returns the `date` and `patch` (patch id) for each summary.
   */
  const getEngineerPatches = async (): Promise<CalendarEngineerSummary[]> => {
    const response = await fetchEngineerSummary(
      engineerId,
      dates.start.toISODate(),
      dates.end.toISODate()
    );

    return response?.data?.map(({ date, patch, startTime, endTime }) => {
      const currentWeekdayIndex = DateTime.fromISO(date).weekday;
      const weekdays = Info.weekdays('long');

      return {
        date,
        patch,
        startTime,
        endTime,
        weekday: weekdays[currentWeekdayIndex]
      };
    });
  };

  const fetchAvailabilityUtilisation = async () => {
    const params = {
      startDate: dates.start.toISODate(),
      endDate: dates.end.toISODate(),
      engineerId
    };
    const response = await getAvailabilityUtilisation(params);

    const sortedResponses: EngineerDailyUtilisation[] = response.sort(
      (a, b) =>
        DateTime.fromISO(a.date).toMillis() -
        DateTime.fromISO(b.date).toMillis()
    );

    return sortedResponses;
  };

  useEffect(() => {
    let fetchPatches = true;

    setCalendarAvailability(state => ({ ...state, isLoading: true }));
    getEngineerPatches()
      .then(patches => {
        setEngineerSummaries(patches);

        return fetchAvailabilityUtilisation().then(dailyUtilisation => {
          // We don't want to update state if the component has been unmounted
          if (fetchPatches) {
            setCalendarAvailability(state => ({
              ...state,
              isLoading: false,
              data: dailyUtilisation
            }));
          }
        });
      })
      .catch(err => {
        if (fetchPatches) {
          setCalendarAvailability(() => ({
            isLoading: false,
            data: undefined,
            error: true
          }));

          handleError(err, dispatch);
        }
      });

    return () => {
      fetchPatches = false;
    };
  }, [dates.start, dates.end]);

  const engineerData = useEngineerData(engineerId);

  const appointmentsDateMapper = ({
    id,
    reason,
    slot
  }: Pick<Appointment, 'id' | 'reason' | 'slot'>) => {
    const date = DateTime.fromISO(slot.date);
    const startTime = DateTime.fromISO(`${slot.date}T${slot.startTime}`);
    const endTime = DateTime.fromISO(`${slot.date}T${slot.endTime}`);

    return {
      id,
      reason,
      slot: {
        date,
        startTime,
        endTime
      }
    };
  };

  const fixedAppointments = useAppointmentsData();

  const memoizedEngineerAppointments = useMemo(
    () => fixedAppointments.map(appointmentsDateMapper),
    [fixedAppointments]
  );

  useEffect(() => {
    dispatch(fetchAllEngineerFixedAppointments(engineerId));
    dispatch(fetchAllSkills());
  }, []);

  useEffect(() => {
    setEvents(memoizedEngineerAppointments);
  }, [memoizedEngineerAppointments]);

  return (
    <section className={classes.container} data-testid="engineerCalendar">
      <Paper elevation={2}>
        <EngineerCalendarContext.Provider
          value={{
            calendarAvailability,
            setCalendarAvailability,
            engineerData,
            engineerSummaries
          }}
        >
          <TemporaryAdjustmentFormContextProvider>
            <EngineerCalendarHeader />
            <EngineerCalendarBody events={events} />
            <EngineerCalendarKey />
            <AdjustmentFormModals />
          </TemporaryAdjustmentFormContextProvider>
        </EngineerCalendarContext.Provider>
      </Paper>
    </section>
  );
}

export default EngineerCalendar;
